/** Companies (`/api/companies/*`): an employer and the parts its cases use. */

export const CONTACT_ROLES = [
  "primary",
  "approver",
  "requester",
  "signatory",
  "billing"
] as const;
export type ContactRole = (typeof CONTACT_ROLES)[number];

export const PORTAL_ACCESS = [
  "all",
  "requested",
  "sign",
  "spend",
  "none"
] as const;
export type PortalAccess = (typeof PORTAL_ACCESS)[number];

export type CompanyAddress = {
  street: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
};

export type Signatory = { name: string; title: string; email: string };

export type CompanyEntity = {
  id: string;
  legal_name: string;
  dba: string;
  fein: string;
  address: CompanyAddress;
  naics: string;
  us_employees: number | null;
  signatory: Signatory | null;
  is_default: boolean;
};

export type Worksite = {
  id: string;
  name: string;
  address: CompanyAddress;
  remote: boolean;
};

export type JobDescription = {
  id: string;
  title: string;
  soc_code: string;
  wage_level: string;
  duties: string;
};

export type CompanyContact = {
  id: string;
  name: string;
  title: string;
  email: string;
  phone: string;
  roles: ContactRole[];
  portal_access: PortalAccess;
  notices: string[];
};

export type ProposedValue = {
  id: string;
  field: "fein";
  entity_id: string | null;
  value: string;
  individual_id: string | null;
  source: string;
  created_at: string | null;
};

export type Deadline = { title: string; due_date: string };

export const PLAYBOOK_CATEGORIES = [
  "filing",
  "communication",
  "drafting",
  "approvals",
  "green_card"
] as const;
export type PlaybookCategory = (typeof PLAYBOOK_CATEGORIES)[number];

export const PLAYBOOK_AGENTS = [
  "case_agent",
  "drafting",
  "practice_manager"
] as const;
export type PlaybookAgent = (typeof PLAYBOOK_AGENTS)[number];

/**
 * A standing instruction agents follow on the company's cases. `number` is
 * stable and never reused, so "Northwind 7" means the same rule everywhere;
 * a suggestion has number 0 until an admin adds it.
 */
export type PlaybookRule = {
  id: string;
  number: number;
  category: PlaybookCategory;
  text: string;
  /** Visa types; empty means every case. */
  applies_to: string[];
  source: string;
  agents: PlaybookAgent[];
  enabled: boolean;
  status: "active" | "suggested";
  reason: string;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
  /** "Northwind 7"; null for a suggestion. */
  label?: string | null;
};

/** What a PUT/POST may set on a rule (`number` only restores a deleted one). */
export type PlaybookRuleInput = Partial<
  Pick<
    PlaybookRule,
    | "id"
    | "number"
    | "category"
    | "text"
    | "applies_to"
    | "source"
    | "agents"
    | "enabled"
    | "reason"
  >
>;

export type PlaybookPayload = {
  company_id: string;
  rules: PlaybookRule[];
  path: string;
};

/** The categorizer's proposal for a rule typed in plain English. */
export type RuleDraft = {
  category: PlaybookCategory;
  applies_to: string[];
  agents: PlaybookAgent[];
  text: string;
  /** The model didn't answer; keywords chose. */
  guessed: boolean;
};

/** What the case agent reads for one case (the Playbook tab's side panel). */
export type PlaybookPreview = {
  individual_id: string;
  visa: string | null;
  path: string;
  text: string;
  rules: Array<{ id: string; number: number; on: boolean }>;
  live_total: number;
};

/** What the company's HR portal offers; access per contact is `portal_access`. */
export type CompanyPortalSwitches = {
  requests: boolean;
  docs: boolean;
  spend: boolean;
  messages: boolean;
  approver_contact_id: string | null;
};

export type Company = {
  id: string;
  law_firm_id: string;
  name: string;
  client_no: string;
  responsible_user_id: string | null;
  entities: CompanyEntity[];
  worksites: Worksite[];
  job_descriptions: JobDescription[];
  contacts: CompanyContact[];
  domains: string[];
  notes: string;
  proposals: ProposedValue[];
  playbook: PlaybookRule[];
  portal: CompanyPortalSwitches;
  created_at: string | null;
  updated_at: string | null;
  case_count: number;
};

export type CompanyListRow = Company & {
  open_cases: number;
  next_deadline: Deadline | null;
  /** Null until the company's billing terms are saved. */
  export_format: ExportFormat | null;
  export_grouping: ExportGrouping | null;
  /** The company's share of released time, fees and costs not exported; null while time tracking is off. */
  unbilled: number | null;
  /** HR portal case requests waiting on the firm. */
  open_requests: number;
};

/** What a PUT may change. Nested items without an id are new. */
export type CompanyPatch = Partial<
  Pick<
    Company,
    "name" | "client_no" | "responsible_user_id" | "domains" | "notes"
  >
> & {
  entities?: Array<Partial<CompanyEntity>>;
  worksites?: Array<Partial<Worksite>>;
  job_descriptions?: Array<Partial<JobDescription>>;
  contacts?: Array<Partial<CompanyContact>>;
  portal?: Partial<CompanyPortalSwitches>;
};

export type MatterIds = {
  client_matter_id: string | null;
  po: string | null;
  cost_center: string | null;
};

export type CompanyCaseRow = MatterIds & {
  individual_id: string;
  foreign_national_id: string | null;
  name: string;
  visa: string | null;
  open: boolean;
  /** False for a dependent's case shown through its principal. */
  linked: boolean;
  entity_id: string | null;
  entity: string | null;
  job_description_id: string | null;
  job: string | null;
  worksite_id: string | null;
  worksite: string | null;
  lawyer_ids: string[];
  next_deadline: Deadline | null;
  dependent_of: string | null;
  fee: CaseFee | null;
};

/** The case's fee and split, and what counts against a not-to-exceed amount. */
export type CaseFee = {
  arrangement: "flat_fee" | "hourly_capped" | "hourly" | "non_billable";
  /** The flat fee, or the cap for `hourly_capped`. */
  amount: number | null;
  employer_share: number;
  /** Follows the company's fee schedule (false once the case overrides it). */
  company_terms: boolean;
  spent: number;
};

export type CaseCompanyContext = {
  company: Company | null;
  link: {
    entity: CompanyEntity | null;
    job_description: JobDescription | null;
    /** The job description's title, or the free-text title HR asked with. */
    job_title?: string | null;
    worksite: Worksite | null;
  } | null;
  matter: MatterIds;
  fee?: CaseFee | null;
  /** Why the employer pays this case's fees by law, when it does. */
  fee_lock?: string | null;
};

export type CaseLinkPatch = Partial<
  MatterIds & {
    entity_id: string | null;
    job_description_id: string | null;
    worksite_id: string | null;
  }
>;

export type LinkOptions = {
  entity_id?: string | null;
  job_description_id?: string | null;
  worksite_id?: string | null;
};

export type CaseMatch = {
  individual_id: string;
  name: string;
  visa: string | null;
  /** Null for a case found by its client's name with no employer typed. */
  employer: string | null;
  exact: boolean;
  /** What matched the search: the employer typed on the case, or the client. */
  match?: "employer" | "client";
};

export type EmployerSeed = {
  legalBusinessName: string;
  dba: string;
  contactName: string;
  contactTitle: string;
  contactEmail: string;
  contactPhone: string;
  businessAddress: CompanyAddress;
};

export type MigrationGroup = {
  id: string;
  name: string;
  key: string;
  address_key: string;
  seed: EmployerSeed;
  cases: Array<{ individual_id: string; name: string; visa: string | null }>;
  individual_ids: string[];
  existing_company_id: string | null;
};

export type NearMatch = {
  group_ids: [string, string];
  names: [string, string];
  reason: string;
};

export type MigrationProposals = {
  groups: MigrationGroup[];
  near: NearMatch[];
  skipped: { linked: number; no_employer: number };
};

export type MigrationDecision = {
  group_ids: string[];
  name?: string;
  company_id?: string | null;
};

export type MigrationResult = {
  company_id: string | null;
  name: string;
  created: boolean;
  group_ids: string[];
  individual_ids: string[];
  linked?: string[];
};

export type ImportRowStatus = "ok" | "error" | "unmatched" | "skipped";

export type ImportReport = {
  dry_run: boolean;
  companies: Array<{
    name: string;
    company_id: string | null;
    action: "create" | "update";
    added: { entities: number; contacts: number; job_descriptions: number };
    employees: string[];
    linked?: string[];
  }>;
  rows: Array<{
    row: number;
    company?: string;
    status: ImportRowStatus;
    message?: string;
    individual_ids?: string[];
  }>;
};

// ---------------------------------------------------------------------------
// Billing terms
// ---------------------------------------------------------------------------

export const FEE_BASES = ["flat", "hourly_capped", "hourly"] as const;
export type FeeBasis = (typeof FEE_BASES)[number];

export const EXPORT_FORMATS = ["ledes_1998b", "ledes_1998bi", "csv"] as const;
export type ExportFormat = (typeof EXPORT_FORMATS)[number];

export type ExportGrouping = "matter" | "entity";
export type PoField = "description" | "po_number";
export type RateStatus = "approved" | "pending" | "none";

export type FeeScheduleRow = {
  id: string;
  /** A visa value, or `RFE/NOID` for response work. */
  case_type: string;
  label: string;
  basis: FeeBasis;
  amount: number | null;
  bill_on: string;
  employer_share: number;
};

export type ExportSetup = {
  grouping: ExportGrouping;
  format: ExportFormat;
  ebilling_system: string;
  require_client_matter_id: boolean;
  client_matter_id_format: string;
  require_po: boolean;
  po_field: PoField;
};

export type ApprovedRate = {
  user_id: string;
  rate: number | null;
  status: RateStatus;
  effective_from: string | null;
  effective_to: string | null;
  submitted_at: string | null;
  submitted_by: string | null;
};

export type BudgetLine = { cost_center: string; name: string; amount: number };

export type BillingTimekeeper = {
  user_id: string;
  name: string;
  role: string | null;
  classification: string | null;
  standard_rate: number | null;
};

export type CompanyBilling = {
  company_id: string;
  fee_schedule: FeeScheduleRow[];
  export: ExportSetup;
  approved_rates: ApprovedRate[];
  rate_sheet_approved_on: string | null;
  rate_sheet_next_due: string | null;
  allocations: {
    gl_account: string;
    cost_center_source: "department" | "case";
    cost_centers: string[];
  };
  budget: { fiscal_year: string; total: number | null; lines: BudgetLine[] };
  updated_at: string | null;
  /** Case type -> the rule that makes the employer pay. */
  locks: Record<string, string>;
  response_case_type: string;
  ebilling_systems: string[];
  /** Visa types of the linked cases. */
  case_types: string[];
  timekeepers: BillingTimekeeper[];
  missing_client_matter_ids: number;
  /** The firm's LEDES ID, sent as LAW_FIRM_ID. */
  firm_ledes_id: string | null;
  spend: {
    billed: number;
    unbilled: number;
    by_cost_center: Record<string, number>;
  };
};

export type BillingPatch = Partial<
  Pick<
    CompanyBilling,
    | "export"
    | "rate_sheet_approved_on"
    | "rate_sheet_next_due"
    | "allocations"
    | "budget"
  >
> & {
  fee_schedule?: Array<Partial<FeeScheduleRow>>;
  approved_rates?: Array<Partial<ApprovedRate>>;
};

export type ExportIssue = {
  kind: string;
  id: string;
  label: string;
  message: string;
  severity: "error" | "warn";
  entry_ids?: string[];
  fix?: { action: "leave_out" | "price_at_approved_rate"; label: string };
};

export type NextExportLine = {
  individual_id: string;
  case_label: string;
  matter_no: string;
  client_matter_id: string;
  po: string;
  cost_center: string;
  share: number;
  hours: number;
  amount: number;
  basis: string;
};

export type NextExportInvoice = {
  number: string;
  label: string;
  client_name: string;
  client_no: string;
  payer: "company" | "employee" | "client";
  total: number;
  lines: NextExportLine[];
  issues: ExportIssue[];
};

export type NextExport = {
  enabled: boolean;
  format?: ExportFormat;
  format_label?: string;
  grouping?: ExportGrouping;
  count?: number;
  hours?: number;
  amount?: number;
  employee_amount?: number;
  included_released?: boolean;
  invoices: NextExportInvoice[];
  issues: ExportIssue[];
  blocking?: boolean;
};

// ---------------------------------------------------------------------------
// The HR portal, seen from the firm
// ---------------------------------------------------------------------------

export type ContactLoginStatus = "invited" | "active" | "revoked";

export type ContactLogin = {
  status: ContactLoginStatus;
  invited_at: string | null;
  accepted_at: string | null;
  last_seen_at: string | null;
};

export type PortalPerson = {
  kind: "contact" | "firm" | "system";
  id: string;
  name: string;
  at: string | null;
};

export type RequestNote = { id: string; by: PortalPerson; text: string };

export type CaseRequestStatus =
  | "pending_approval"
  | "pending_firm"
  | "accepted"
  | "declined";

export type RequestConflict = {
  kind: "existing_client" | "same_name";
  foreign_national_id: string;
  name: string;
  email: string;
};

export type FirmCaseRequest = {
  id: string;
  status: CaseRequestStatus;
  employee_name: string;
  employee_email: string;
  job_title: string;
  case_type: string;
  cost_center: string;
  po: string;
  client_matter_id: string;
  notes: string;
  estimate: { amount?: number | null; basis?: string; employer_share?: number };
  requested_by: PortalPerson;
  approved_by: PortalPerson | null;
  decided_by: PortalPerson | null;
  decline_reason: string;
  notes_thread: RequestNote[];
  individual_id: string | null;
  foreign_national_id: string | null;
  created_at: string | null;
  entity: string;
  worksite: string;
  lock: string | null;
  rule_count: number;
  conflicts?: RequestConflict[];
  suggested_matter_no?: string | null;
};

export type AcceptRequestBody = {
  team?: string[];
  matter_no?: string;
  acknowledge_conflicts?: boolean;
  send_intake?: boolean;
};

export type AcceptRequestResult = {
  request: FirmCaseRequest;
  individual_id: string;
  foreign_national_id: string;
  case_id: string | null;
  created_client: boolean;
  matter_no: string | null;
  intake_sent: boolean;
};

export type CompanyApprovalKind = "cost" | "draft_review";
export type CompanyApprovalStatus =
  | "draft"
  | "pending"
  | "approved"
  | "declined"
  | "cancelled";

export type CompanyApproval = {
  id: string;
  individual_id: string;
  employee_name: string;
  case_label: string;
  kind: CompanyApprovalKind;
  summary: string;
  amount: number | null;
  attachment: { kind: "draft" | "file"; id: string; name: string } | null;
  status: CompanyApprovalStatus;
  rule_label: string;
  created_by: PortalPerson | null;
  answered_by: PortalPerson | null;
  answer_note: string;
  sent_at: string | null;
  created_at: string | null;
  contact_id?: string | null;
  source?: "assistant" | "rule" | "firm";
};

export type CompanyMessage = {
  id: string;
  individual_id: string;
  author: PortalPerson;
  body: string;
  created_at: string | null;
};
